import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download } from 'lucide-react';
import { canChooseInvoicePdfLocation, saveInvoicePdf } from '@/lib/saveInvoicePdf';

interface InvoicePDFDownloadProps {
  invoiceId: string;
  invoiceNumber: string;
  establishmentName?: string;
  clientName?: string;
  clientLegalName?: string;
  hideDba?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  // R4 (invoice-identity-cascade) AC6: the unresolved `needs a client` gate
  // disables the download. The server (R1) also blocks the PDF path; this is
  // the visible affordance over it.
  disabled?: boolean;
  disabledReason?: string;
  // R5 iteration 4: a download saves an immutable copy on the server; the caller refreshes whatever lists saved copies
  // (the Mark as Sent dialog offers them) so the copy just downloaded can be chosen without reloading the page.
  onDownloaded?: () => void;
}

export function InvoicePDFDownload({
  invoiceId,
  invoiceNumber,
  variant = 'outline',
  size = 'sm',
  disabled = false,
  disabledReason,
  onDownloaded,
}: InvoicePDFDownloadProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [outOfSyncDialogOpen, setOutOfSyncDialogOpen] = useState(false);
  const [prepared, setPrepared] = useState<{ invoiceId: string; blob: Blob; filename: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const generation = useRef(0);
  const requestPending = useRef(false);
  const saveAsRequested = useRef(false);

  useEffect(() => {
    generation.current += 1;
    requestPending.current = false;
    setPrepared(null);
    setIsDownloading(false);
    setIsSaving(false);
    setOutOfSyncDialogOpen(false);
    saveAsRequested.current = false;
    return () => { generation.current += 1; };
  }, [invoiceId]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    toast.success('PDF downloaded successfully');
  };

  const downloadPDF = async (forceRegenerate = false, saveAs = false) => {
    if (disabled || requestPending.current || isSaving) return;
    saveAsRequested.current = saveAs;
    const requestGeneration = generation.current;
    requestPending.current = true;
    setIsDownloading(true);
    try {
      const { blob, filename } = await api.invoices.downloadPDF(invoiceId, forceRegenerate);
      if (generation.current !== requestGeneration) return;
      // Server owns project-address naming and its missing-address fallback.
      const fallbackNumber = invoiceNumber.replace(/[<>:"/\\|?*\x00-\x1f\x7f]/g, '').trim();
      const savedFilename = filename || `invoice-${fallbackNumber || 'download'}.pdf`;
      // Preparation archives a server copy even if the user cancels local saving.
      onDownloaded?.();
      if (saveAs && canChooseInvoicePdfLocation()) {
        setPrepared({ invoiceId, blob, filename: savedFilename });
      } else {
        downloadBlob(blob, savedFilename);
      }
    } catch (error: unknown) {
      if (generation.current !== requestGeneration) return;
      if (typeof error === 'object' && error !== null && 'status' in error && error.status === 409) {
        // PDF is out of sync - show dialog
        setOutOfSyncDialogOpen(true);
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to download PDF');
      }
    } finally {
      if (generation.current === requestGeneration) {
        requestPending.current = false;
        setIsDownloading(false);
      }
    }
  };

  const savePrepared = async () => {
    if (!prepared || prepared.invoiceId !== invoiceId || isSaving) return;
    const saveGeneration = generation.current;
    setIsSaving(true);
    try {
      const result = await saveInvoicePdf(prepared.blob, prepared.filename);
      if (generation.current !== saveGeneration) return;
      if (result === 'saved') {
        setPrepared(null);
        toast.success('PDF saved successfully');
      }
    } catch (error) {
      if (generation.current === saveGeneration) {
        toast.error(error instanceof Error ? error.message : 'Could not save PDF. Try again.');
      }
    } finally {
      if (generation.current === saveGeneration) setIsSaving(false);
    }
  };

  const handleRegenerateAndDownload = async () => {
    setOutOfSyncDialogOpen(false);
    await downloadPDF(true, saveAsRequested.current);
  };

  return (
    <>
      <div className="inline-flex flex-wrap items-center gap-1">
        <Button
          variant={variant}
          size={size}
          onClick={() => downloadPDF(false)}
          disabled={isDownloading || isSaving || disabled}
          title={disabled ? disabledReason : undefined}
          data-testid="download-pdf-button"
        >
          <Download className="mr-2 h-4 w-4" />
          {isDownloading && !saveAsRequested.current ? 'Downloading...' : 'Download PDF'}
        </Button>
        {canChooseInvoicePdfLocation() && (
          <Button
            variant="ghost"
            size={size}
            onClick={() => downloadPDF(false, true)}
            disabled={isDownloading || isSaving || disabled}
            title={disabled ? disabledReason : 'Choose a filename and folder'}
            data-testid="save-as-pdf-button"
          >
            {isDownloading && saveAsRequested.current ? 'Preparing...' : 'Save As...'}
          </Button>
        )}
      </div>

      <Dialog open={prepared?.invoiceId === invoiceId} onOpenChange={(open) => { if (!open && !isSaving) setPrepared(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save PDF</DialogTitle>
            <DialogDescription>Choose a name and folder for this copy on your computer.</DialogDescription>
          </DialogHeader>
          <p className="break-words text-sm font-medium">{prepared?.filename}</p>
          <p className="text-sm text-muted-foreground">Renaming or closing here leaves the saved copy in invoice history unchanged.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={isSaving} onClick={() => setPrepared(null)}>Close</Button>
            <Button disabled={isSaving} onClick={savePrepared}>{isSaving ? 'Saving...' : 'Save as'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Out-of-Sync Dialog */}
      <Dialog open={outOfSyncDialogOpen} onOpenChange={setOutOfSyncDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PDF Out of Sync</DialogTitle>
            <DialogDescription>
              The invoice data has been modified since the PDF was last generated.
              Would you like to regenerate the PDF with the latest data?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOutOfSyncDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateAndDownload}
              disabled={isDownloading}
            >
              {isDownloading ? 'Regenerating...' : 'Regenerate & Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
